import React, { useState, useEffect } from 'react';
import './Admin.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [actionLoading, setActionLoading] = useState({});

  // Helper to get auth token
  const getAuthToken = () => {
    try {
      // Token is stored directly as 'token' key (not nested in 'auth' object)
      const token = localStorage.getItem('token');
      return token;
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
    return null;
  };

  // Helper for API calls
  const apiCall = async (endpoint, options = {}) => {
    const token = getAuthToken();

    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(errorData.detail || `Request failed with status ${response.status}`);
    }

    return response.json();
  };

  // Fetch users data
  const fetchUsers = async () => {
    try {
      setLoading(true);

      const [allUsersData, pendingUsersData] = await Promise.all([
        apiCall('/api/auth/admin/users'),
        apiCall('/api/auth/admin/users/pending')
      ]);

      setUsers(allUsersData.users || []);
      setPendingUsers(pendingUsersData.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to fetch users' });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Approve user
  const handleApprove = async (email) => {
    try {
      setActionLoading({ ...actionLoading, [email]: 'approving' });
      await apiCall(`/api/auth/admin/users/${encodeURIComponent(email)}/approve`, {
        method: 'PUT'
      });

      setMessage({ type: 'success', text: `User ${email} approved successfully` });
      setTimeout(() => setMessage(null), 3000);

      // Refresh data
      await fetchUsers();
      setActionLoading({ ...actionLoading, [email]: null });
    } catch (err) {
      console.error('Error approving user:', err);
      setMessage({ type: 'error', text: `Failed to approve user: ${err.message}` });
      setTimeout(() => setMessage(null), 5000);
      setActionLoading({ ...actionLoading, [email]: null });
    }
  };

  // Reject user
  const handleReject = async (email) => {
    if (!window.confirm(`Are you sure you want to reject user ${email}?`)) {
      return;
    }

    try {
      setActionLoading({ ...actionLoading, [email]: 'rejecting' });
      await apiCall(`/api/auth/admin/users/${encodeURIComponent(email)}/reject`, {
        method: 'PUT'
      });

      setMessage({ type: 'success', text: `User ${email} rejected` });
      setTimeout(() => setMessage(null), 3000);

      // Refresh data
      await fetchUsers();
      setActionLoading({ ...actionLoading, [email]: null });
    } catch (err) {
      console.error('Error rejecting user:', err);
      setMessage({ type: 'error', text: `Failed to reject user: ${err.message}` });
      setTimeout(() => setMessage(null), 5000);
      setActionLoading({ ...actionLoading, [email]: null });
    }
  };

  // Delete user
  const handleDelete = async (email) => {
    if (!window.confirm(`Are you sure you want to delete user ${email}? This action cannot be undone.`)) {
      return;
    }

    try {
      setActionLoading({ ...actionLoading, [email]: 'deleting' });
      await apiCall(`/api/auth/admin/users/${encodeURIComponent(email)}`, {
        method: 'DELETE'
      });

      setMessage({ type: 'success', text: `User ${email} deleted successfully` });
      setTimeout(() => setMessage(null), 3000);

      // Refresh data
      await fetchUsers();
      setActionLoading({ ...actionLoading, [email]: null });
    } catch (err) {
      console.error('Error deleting user:', err);
      setMessage({ type: 'error', text: `Failed to delete user: ${err.message}` });
      setTimeout(() => setMessage(null), 5000);
      setActionLoading({ ...actionLoading, [email]: null });
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Render user table
  const renderUserTable = (userList) => {
    if (userList.length === 0) {
      return (
        <div className="no-data">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          <p>No users found</p>
        </div>
      );
    }

    return (
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Company</th>
              <th>Role</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {userList.map((user) => (
              <tr key={user.email}>
                <td>
                  <div className="user-name">{user.name}</div>
                </td>
                <td>{user.email}</td>
                <td>{user.company || '-'}</td>
                <td>
                  <span className={`badge badge-${user.role}`}>
                    {user.role}
                  </span>
                </td>
                <td>
                  <span className={`status-badge status-${user.account_status}`}>
                    {user.account_status === 'approved' && '✓ Approved'}
                    {user.account_status === 'pending' && '⏱ Pending'}
                    {user.account_status === 'rejected' && '✗ Rejected'}
                  </span>
                </td>
                <td><span className="text-muted">{formatDate(user.created_at)}</span></td>
                <td>
                  <div className="action-buttons">
                    {user.account_status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(user.email)}
                          disabled={actionLoading[user.email]}
                          className="btn-action btn-approve"
                          title="Approve user"
                        >
                          {actionLoading[user.email] === 'approving' ? '...' : '✓ Approve'}
                        </button>
                        <button
                          onClick={() => handleReject(user.email)}
                          disabled={actionLoading[user.email]}
                          className="btn-action btn-reject"
                          title="Reject user"
                        >
                          {actionLoading[user.email] === 'rejecting' ? '...' : '✗ Reject'}
                        </button>
                      </>
                    )}
                    {user.role !== 'admin' && (
                      <button
                        onClick={() => handleDelete(user.email)}
                        disabled={actionLoading[user.email]}
                        className="btn-action btn-delete"
                        title="Delete user"
                      >
                        {actionLoading[user.email] === 'deleting' ? '...' : 'Delete'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>User Management</h1>
        <p>Manage user accounts and approval requests</p>
      </div>

      {message && (
        <div className={`settings-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{users.length}</span>
            <span className="stat-label">Total Users</span>
          </div>
        </div>

        <div className="stat-card stat-pending">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{pendingUsers.length}</span>
            <span className="stat-label">Pending Approval</span>
          </div>
        </div>

        <div className="stat-card stat-approved">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">
              {users.filter(u => u.account_status === 'approved').length}
            </span>
            <span className="stat-label">Approved</span>
          </div>
        </div>
      </div>

      <div className="settings-layout">
        {/* Sidebar */}
        <div className="settings-sidebar">
          <button
            className={`sidebar-tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            <span className="tab-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </span>
            Pending Approval ({pendingUsers.length})
          </button>
          <button
            className={`sidebar-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            <span className="tab-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </span>
            All Users ({users.length})
          </button>
        </div>

        {/* Content */}
        <div className="settings-content">
          <div className="section-header-with-action">
            <div>
              <h2>{activeTab === 'pending' ? 'Pending Approval Requests' : 'All Users'}</h2>
              <p className="section-description">
                {activeTab === 'pending'
                  ? 'Review and approve user registration requests'
                  : 'View and manage all user accounts'}
              </p>
            </div>
            <button onClick={fetchUsers} className="btn-secondary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              Refresh
            </button>
          </div>

          {activeTab === 'pending' ? renderUserTable(pendingUsers) : renderUserTable(users)}
        </div>
      </div>
    </div>
  );
};

export default Admin;
